import { Drawer } from "antd";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { useMemo } from "react";
import styled, { createGlobalStyle } from "styled-components";
import * as Yup from "yup";
import { ButtonT1 } from "../../../components/atoms/Button";
import { FormLabel } from "../../../components/atoms/label";
import { PortalFooter } from "../../../components/molecules/Footer";
import { PortalHeader } from "../../../components/molecules/Header";
import { portalDrawerAntHeaderCss } from "../../../components/molecules/Header/Header.styles";
import { FormControlInput } from "../../../components/organisms/Form";
import {
  FormControlDate,
  FormControlSelect,
} from "../../../components/organisms/Form/control";
import { ManageGeneralFormProvider } from "../../../components/organisms/Provider/ManageGeneralFormProvider";
import { useNotification } from "../../../hooks/useNotification";
import { getApiErrorMessage } from "../../../helper/apiErrorMessage";
import { useCreateCustomerPaymentMutation } from "../../../store/apiSlice/invoiceApiSlice";

const DrawerHeaderStyle = createGlobalStyle`
  ${portalDrawerAntHeaderCss}
`;

const Body = styled.div`
  padding: 0 24px 24px;
`;

const ReadonlyRow = styled.div`
  margin-bottom: 16px;
  font-size: 14px;
  color: #374151;
`;

const schema = Yup.object().shape({
  reference: Yup.string().required("Reference is required"),
  date_paid: Yup.mixed().required("Payment date is required"),
  amount: Yup.number().required("Amount is required").positive("Amount must be greater than 0"),
  bank_account_dummy_id: Yup.string().required("Bank account is required"),
  allocated_amount: Yup.number()
    .required("Allocated amount is required")
    .positive("Amount must be greater than 0"),
});

const saleInvoiceAmountDue = invoice => {
  if (!invoice) return 0;
  if (invoice.amount_due != null && invoice.amount_due !== "") {
    return Number(invoice.amount_due);
  }
  const total = Number(invoice.total_amount) || 0;
  const paid = Number(invoice.amount_paid) || 0;
  return Math.max(total - paid, 0);
};

export function RecordCustomerPaymentModal({ open, onClose, invoice, onRecorded }) {
  const notify = useNotification();
  const [createPayment, { isLoading }] = useCreateCustomerPaymentMutation();

  const amountDue = useMemo(() => saleInvoiceAmountDue(invoice), [invoice]);
  const customerId = invoice?.customer?.id ?? invoice?.customer_id;
  const currencyId = invoice?.currency?.id ?? invoice?.currency_id;

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      reference: "",
      date_paid: dayjs(),
      amount: amountDue > 0 ? amountDue : "",
      bank_account_dummy_id: "",
      allocated_amount: amountDue > 0 ? amountDue : "",
    },
    validationSchema: schema,
    onSubmit: async values => {
      if (!invoice?.id || !customerId || !currencyId) {
        notify({ type: "error", message: "Invoice details are incomplete." });
        return;
      }
      const alloc = Number(values.allocated_amount);
      const payAmount = Number(values.amount);
      if (alloc > amountDue) {
        notify({
          type: "error",
          message: `Allocation cannot exceed amount due (${amountDue}).`,
        });
        return;
      }
      if (alloc > payAmount) {
        notify({
          type: "error",
          message: "Allocated amount cannot exceed payment amount.",
        });
        return;
      }
      try {
        await createPayment({
          customer_id: customerId,
          currency_id: currencyId,
          amount: payAmount,
          date_paid: dayjs(values.date_paid).format("YYYY-MM-DD"),
          reference: values.reference.trim(),
          bank_account_dummy: String(values.bank_account_dummy_id),
          allocations: [
            {
              sale_invoice_id: invoice.id,
              allocated_amount: alloc,
            },
          ],
        }).unwrap();
        notify({ type: "success", message: "Payment recorded successfully." });
        onRecorded?.();
        onClose?.();
      } catch (err) {
        notify({ type: "error", message: getApiErrorMessage(err) });
      }
    },
  });

  return (
    <>
      <DrawerHeaderStyle />
      <Drawer
        open={open}
        onClose={onClose}
        width={480}
        destroyOnClose
        title={null}
        closable={false}
        styles={{ body: { padding: 0 } }}
      >
        <PortalHeader title="Record payment" onClose={onClose} />
        <ManageGeneralFormProvider formik={formik}>
          <Body>
            <ReadonlyRow>
              <FormLabel>Invoice</FormLabel>
              <div>{invoice?.name}</div>
            </ReadonlyRow>
            <ReadonlyRow>
              <FormLabel>Customer</FormLabel>
              <div>{invoice?.customer_name || invoice?.customer?.display_name}</div>
            </ReadonlyRow>
            <ReadonlyRow>
              <FormLabel>Amount due</FormLabel>
              <div>{amountDue}</div>
            </ReadonlyRow>
            <div className="mb-4">
              <FormControlInput
                name="amount"
                type="number"
                label="Payment amount"
                placeholder="Enter amount"
                required
                context="general"
              />
            </div>
            <div className="mb-4">
              <FormControlInput
                name="allocated_amount"
                type="number"
                label="Apply to this invoice"
                placeholder="Enter amount"
                required
                context="general"
              />
            </div>
            <div className="mb-4">
              <FormControlSelect
                optionKey="bankAccount"
                label="Bank account"
                name="bank_account_dummy_id"
                placeholder="Select bank account"
                required
                context="general"
              />
            </div>
            <div className="mb-4">
              <FormControlInput
                name="reference"
                type="text"
                label="Reference"
                placeholder="Enter reference"
                required
                context="general"
              />
            </div>
            <div className="mb-4">
              <FormControlDate
                label="Payment date"
                name="date_paid"
                placeholder="Select date"
                required
                context="general"
              />
            </div>
          </Body>
          <PortalFooter>
            <ButtonT1
              title="Cancel"
              $variant="primaryLight"
              onClickCallback={onClose}
              disabled={isLoading}
            />
            <ButtonT1
              title="Record payment"
              onClickCallback={() => formik.submitForm()}
              loading={isLoading}
            />
          </PortalFooter>
        </ManageGeneralFormProvider>
      </Drawer>
    </>
  );
}
